/**
 * Shared Notifications Hook
 * Centralizes notification fetching and actions to ensure consistency
 * across all components (regular users, B2B users, etc.)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios, { API } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

const API_BASE = API.replace('/api', '');

export const useNotifications = (options = {}) => {
  const { autoFetch = true, limit = 50 } = options;
  const { user, loading: authLoading } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use refs for values that shouldn't trigger re-renders
  const apiBaseRef = useRef(API_BASE);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    // Wait for auth to be ready
    if (authLoading) return [];
    if (!user) {
      setLoading(false);
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Use user from AuthContext instead of making a separate /auth/me call
      // B2B users get their client's notifications, regular users get admin notifications
      const endpoint = user?.client_id 
        ? `${apiBaseRef.current}/api/client-notifications/active`
        : `${apiBaseRef.current}/api/notifications`;
      
      const res = await axios.get(endpoint, {
        withCredentials: true
      });
      setNotifications(res.data.notifications || []);
      return res.data.notifications || [];
    } catch (err) {
      // Silently fail - notifications are non-critical
      console.debug('Notifications fetch failed:', err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [authLoading, user]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await axios.post(`${apiBaseRef.current}/api/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      return true;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      return false;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await axios.post(`${apiBaseRef.current}/api/notifications/mark-all-read`, {}, {
        withCredentials: true
      });
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      return true;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return false;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await axios.delete(`${apiBaseRef.current}/api/notifications/${notificationId}`, {
        withCredentials: true
      });
      // Update local state
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      return true;
    } catch (err) {
      console.error('Error deleting notification:', err);
      return false;
    }
  }, []);

  const dismissNotification = useCallback(async (notificationId) => {
    try {
      await axios.post(`${apiBaseRef.current}/api/notifications/${notificationId}/dismiss`, {}, {
        withCredentials: true
      });
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_dismissed: true } : n)
      );
      return true;
    } catch (err) {
      console.error('Error dismissing notification:', err);
      return false;
    }
  }, []);

  const trackClick = useCallback(async (notificationId) => {
    try {
      await axios.post(`${apiBaseRef.current}/api/notifications/${notificationId}/click`, {}, {
        withCredentials: true
      });
      return true;
    } catch (err) {
      console.error('Error tracking notification click:', err);
      return false;
    }
  }, []);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
    }
  }, [autoFetch, fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    dismissNotification,
    trackClick,
    // Helper to find a specific notification
    getNotificationById: (id) => notifications.find(n => n.notification_id === id)
  };
};

export default useNotifications;
